import { API_ENDPOINT, API_STAGE } from '../appConfig';
import { S3_KEYS, resolveS3Key } from '../constants/s3Keys';

let authToken = '';

export function setAuthToken(token: string) {
   authToken = token;
}

export async function callApi(payload: Record<string, unknown>): Promise<any> {
   const apiRoute = payload.apiRoute as string;
   const url = API_ENDPOINT + apiRoute;

   const response = await fetch(url, {
       method: 'POST',
       headers: {
           'Content-Type': 'application/json',
           ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
       },
       body: JSON.stringify(payload),
   });

   if (!response.ok) {
       throw new Error(`API error: ${response.status}`);
   }

   const text = await response.text();
   const sanitized = text
       .replace(/: *NaN/g, ': null')
       .replace(/: *Infinity/g, ': null')
       .replace(/: *-Infinity/g, ': null');

   const data = JSON.parse(sanitized);
   if (data.body) {
       return typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
   }
   return data;
}

export function getNodeConfig(tableName: string) {
   return callApi({
       method: 'get_data_for_table',
       apiRoute: `${API_STAGE}/ds-read`,
       table_name: tableName,
   });
}

export function getDSData(tableName: string, options?: Record<string, unknown>) {
   return callApi({
       method: 'get_data_for_table',
       apiRoute: `${API_STAGE}/ds-read`,
       table_name: tableName,
       ...options,
   });
}

export async function getDSDataPaginated(tableName: string, options?: Record<string, unknown>, limit: number = 2000): Promise<any[]> {
    let allItems: any[] = [];
    let exclusiveStartKey: unknown = undefined;

    while (true) {
        const payload: Record<string, unknown> = {
            method: 'get_data_for_table',
            apiRoute: `${API_STAGE}/ds-read`,
            table_name: tableName,
            limit,
            ...options,
        };
        if (exclusiveStartKey) {
            payload.exclusive_start_key = exclusiveStartKey;
        }
        const result = await callApi(payload);
        const items = Array.isArray(result?.items) ? result.items : Array.isArray(result) ? result : [];
        allItems = allItems.concat(items);

        // Stop if no more pages
        if (!result?.last_evaluated_key) break;
        exclusiveStartKey = result.last_evaluated_key;
    }

    return allItems;
}

export function bulkUpdateDSData(
   tableName: string,
   records: Array<{ ofd_date: string; location_id: string; cycle_name: string; internal_sort_code?: string; cpt?: string; ship_method?: string; updates: Record<string, unknown> }>,
) {
   return callApi({
       method: 'update_ds_values',
       apiRoute: `${API_STAGE}/ds-write`,
       table_name: tableName,
       operation_type: 'bulk_update',
       records: records.map(r => ({
           ofd_date: r.ofd_date,
           location_id: r.location_id,
           cycle_name: r.cycle_name,
           ...(r.internal_sort_code != null && { internal_sort_code: r.internal_sort_code }),
           ...(r.cpt != null && { cpt: r.cpt }),
           ...(r.ship_method != null && { ship_method: r.ship_method }),
           updates: r.updates,
       })),
   });
}

const DEFAULT_BATCH_SIZE = 25;

export async function bulkUpdateDSDataInBatches(
   tableName: string,
   records: Array<{ ofd_date: string; location_id: string; cycle_name: string; internal_sort_code?: string; cpt?: string; ship_method?: string; updates: Record<string, unknown> }>,
   options?: { batchSize?: number; onProgress?: (completed: number, total: number) => void },
): Promise<{ successCount: number; failedBatches: number }> {
   const batchSize = options?.batchSize || DEFAULT_BATCH_SIZE;
   const onProgress = options?.onProgress;
   const totalRecords = records.length;
   let successCount = 0;
   let failedBatches = 0;

   for (let i = 0; i < totalRecords; i += batchSize) {
       const batch = records.slice(i, i + batchSize);
       try {
           await bulkUpdateDSData(tableName, batch);
           successCount += batch.length;
       } catch (err) {
           failedBatches++;
           // Continue processing remaining batches even if one fails
       }
       if (onProgress) {
           onProgress(Math.min(i + batchSize, totalRecords), totalRecords);
       }
   }

   if (failedBatches > 0 && successCount === 0) {
       throw new Error(`All ${failedBatches} batch(es) failed to update.`);
   }

   return { successCount, failedBatches };
}

export function getBatchEvents(tableName: string, process: string) {
   return callApi({
       method: 'get_data_for_table',
       apiRoute: `${API_STAGE}/ds-read`,
       table_name: tableName,
       filters: [{ key: 'process', operator: 'eq', value: process }],
   });
}

export function getPresignedUrls(s3Keys: string[]) {
   return callApi({
       method: 'get_presigned_urls',
       apiRoute: `${API_STAGE}/capacity-management`,
       s3_keys: s3Keys,
   });
}

export function getConfig(tableName: string) {
   return callApi({
       method: 'get_config',
       apiRoute: `${API_STAGE}/config`,
       table_name: tableName,
   });
}

export function updateConfig(tableName: string, items: Record<string, unknown>[]) {
   return callApi({
       method: 'update_config',
       apiRoute: `${API_STAGE}/config`,
       table_name: tableName,
       items,
   });
}

export async function uploadCapSettingFiles(
   files: Record<string, File>,
   batchId: string,
   round: string,
   horizon: string = 'D-1',
   program: string = 'AMZL',
) {
   const templates = S3_KEYS[program]?.[horizon]?.cap_setting?.inputs;
   const params = { batchId, round };

   const fileMap: Record<string, File> = {};
   for (const [key, template] of Object.entries(templates)) {
       if (files[key]) fileMap[resolveS3Key(template, params)] = files[key];
   }

   const { urls } = await getPresignedUrls(Object.keys(fileMap));

   await Promise.all(
       Object.entries(fileMap).map(([key, file]) =>
           fetch(urls[key], {
               method: 'PUT',
               body: file,
           }).then(res => {
               if (!res.ok) throw new Error(`Failed to upload ${file.name}`);
           })
       )
   );
}

export function triggerCapacityCalculation(payload: Record<string, unknown>) {
   return callApi({
       ...payload,
       apiRoute: `${API_STAGE}/capacity-management`,
   });
}

export function triggerWeeklyDeployment(startDate: string, endDate: string): Promise<{ batch_id?: string; cm_id?: string; error?: string }> {
   return callApi({
       method: 'weekly_deployment',
       apiRoute: `${API_STAGE}/capacity-management`,
       start_date: startDate,
       end_date: endDate,
   });
}

export function uploadNodeConfig(fileContent: string, uploadedBy: string) {
   return callApi({
       method: 'upload_node_config',
       apiRoute: `${API_STAGE}/config`,
       file_content: fileContent,
       uploaded_by: uploadedBy,
   });
}

export function getPresignedDownloadUrls(s3Keys: string[]) {
   return callApi({
       method: 'get_presigned_download_urls',
       apiRoute: `${API_STAGE}/capacity-management`,
       s3_keys: s3Keys,
   });
}

export function getUserPermissions(): Promise<{ user: string; hasFullAccess: boolean; allowedPages: string[] }> {
   return callApi({
       method: 'get_user_permissions',
       apiRoute: `${API_STAGE}/ds-read`,
   });
}

export function approveTacticalDeployment(
   approvedBy: string,
   compositeKeys?: string[],
) {
   const payload: any = {
       apiRoute: `${API_STAGE}/tactical-deployment`,
       approved_by: approvedBy,
   };
   if (compositeKeys && compositeKeys.length > 0) {
       payload.composite_keys = compositeKeys;
   }
   return callApi(payload);
}
